import React, { useState, useEffect } from "react";
import { Guardian } from "../../../services/Dtos";
import { VStack, HStack, Text, Box } from "@chakra-ui/react";
import { useStudentsContext } from "../../../Contexts/StudentsContext";
import { titles } from "../../../services/constants";
import useThemeColors from "../../../hooks/useThemeColors";
import CustomAlert from "../../CustomUi/CustomAlert";
import useIcons from "../../../hooks/useIcons";
import CustomModal from "../../CustomUi/CustomModal";
import TextInput from "../../CustomUi/TextInput";
import SelectInput from "../../CustomUi/SelectInput";
import { useFormChanges } from "../../../hooks/useFormChanges";
import { useFormFieldHandler } from "../../../hooks/useFormFieldHandler";
import { useGuardianService } from "../../../hooks/Domain/useGuardianService";
import { useConfirmDialog } from "../../../hooks/useConfirmDialog";
import useUnsavedChangesPrompt from "../../../hooks/useUnsavedChangesPrompt";

interface AddGuardianModalProps {
  isOpen: boolean;
  onClose: () => void;
  guardianToEdit: Guardian | null;
}

const AddGuardianModal: React.FC<AddGuardianModalProps> = ({
  isOpen,
  onClose,
  guardianToEdit,
}) => {
  const initialGuardianFormState: Guardian = {
    guardianId: "",
    guardianTitle: "",
    guardianFullName: "",
    guardianPhoneNumber: "",
    guardianEmail: "",
    guardianAddress: "",
  };

  const colors = useThemeColors();
  const { selectedStudent } = useStudentsContext();
  const icons = useIcons();
  const [guardianFormState, setGuardianFormState] = useState<Guardian>(
    initialGuardianFormState
  );
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  // Initialize service hook
  const {
    addGuardian,
    editGuardian,
    deleteGuardian,
    errors,
    clearErrors,
    isLoading,
  } = useGuardianService();

  // Initialize confirmation dialog
  const { confirmUnsavedChanges } = useConfirmDialog();

  // Define field configurations for form validation
  const fieldConfigs = {
    guardianTitle: { type: "select" as const, required: true },
    guardianFullName: { type: "text" as const, required: true, maxLength: 100 },
    guardianPhoneNumber: { type: "phone" as const, required: true },
    guardianEmail: { type: "email" as const, required: true },
    guardianAddress: { type: "text" as const, required: true, maxLength: 200 },
  };

  // Use the form changes hook
  const { hasChanges, validateField, validateAllFields, hasValidationErrors } =
    useFormChanges({
      fields: fieldConfigs,
      initialData: guardianToEdit,
      currentData: guardianFormState,
      isEditMode: !!guardianToEdit,
    });

  // Use the form field handler hook
  const { handleFieldChange } = useFormFieldHandler({
    setFormState: setGuardianFormState,
    setTouchedFields,
  });

  // Use unsaved changes prompt
  useUnsavedChangesPrompt(hasChanges);

  // Real-time validation using the hook's validateField
  const validateFieldByName = (name: string, value: any): string => {
    const fieldKey = name as keyof Guardian;
    return validateField(fieldKey, value);
  };

  // Clear errors when modal opens
  useEffect(() => {
    if (isOpen) {
      clearErrors();
      setTouchedFields(new Set());
    }
  }, [isOpen, clearErrors]);

  useEffect(() => {
    if (guardianToEdit) {
      setGuardianFormState(guardianToEdit);
    } else {
      setGuardianFormState(initialGuardianFormState);
    }
    setTouchedFields(new Set());
  }, [guardianToEdit, isOpen]);

  const showFieldError = (fieldName: string): boolean => {
    const error = validateFieldByName(
      fieldName,
      guardianFormState[fieldName as keyof Guardian]
    );
    return !!(error && touchedFields.has(fieldName));
  };

  const getFieldError = (fieldName: string): string => {
    return validateFieldByName(
      fieldName,
      guardianFormState[fieldName as keyof Guardian]
    );
  };

  const currentValidationErrors = validateAllFields();
  const hasValidationErrorsValue = hasValidationErrors(currentValidationErrors);

  const handleSave = async () => {
    if (hasValidationErrorsValue || !hasChanges || !selectedStudent) return;

    if (guardianToEdit) {
      const response = await editGuardian(
        guardianFormState,
        guardianToEdit.guardianId
      );
      if (response.result.succeeded) {
        onClose();
      }
    } else {
      const response = await addGuardian(guardianFormState, selectedStudent.id);
      if (response.result.succeeded) {
        setGuardianFormState(initialGuardianFormState);
        onClose();
      }
    }
  };



  const handleDeleteGuardian = async () => {
    if (!guardianToEdit) return;

    const response = await deleteGuardian(guardianToEdit.guardianId);
    if (response.result.succeeded) {
      onClose();
    }
  };

  const handleClose = async () => {
    if (hasChanges && guardianToEdit) {
      const confirmed = await confirmUnsavedChanges();
      if (confirmed) {
        clearErrors();
        onClose();
      }
    } else {
      clearErrors();
      onClose();
    }
  };

  const titleOptions = titles.map((title) => ({
    label: title,
    value: title,
  }));

  return (
    <>
      <CustomModal
        isOpen={isOpen}
        onClose={handleClose}
        handleClose={handleClose}
        handleAction={handleSave}
        handleDelete={handleDeleteGuardian}
        isLoading={isLoading}
        hasChanges={hasChanges}
        hasValidationErrors={hasValidationErrorsValue}
        isDisabled={!hasChanges || hasValidationErrorsValue || isLoading}
        title={guardianToEdit ? "Edit Guardian" : "Add New Guardian"}
        icon={icons.people.guardian}
        mode={guardianToEdit ? "edit" : "add"}
        actionText={guardianToEdit ? "Save Changes" : "Save Guardian"}
        deleteMessage="This guardian and all related data will be permanently deleted"
        openDeleteDialog={false}
        setOpenDeleteDialog={() => {}}
      >
        {/* Show only API/server errors */}
        {errors.length > 0 && (
          <VStack gap={2} mb={4}>
            {errors.slice(0, 3).map((error, index) => (
              <CustomAlert key={index} message={error} />
            ))}
          </VStack>
        )}

        <VStack gap={6} align="stretch">
          {/* Guardian Information Section */}
          <Box>
            <Text
              fontSize="lg"
              fontWeight="semibold"
              mb={4}
              color={colors.text.primary}
            >
              Guardian Information
            </Text>
            <VStack gap={4}>
              <HStack
                gap={4}
                w="full"
                flexDirection={["column", "row"]}
                alignItems={["stretch", "start"]}
              >
                <SelectInput
                  label="Title"
                  name="guardianTitle"
                  value={guardianFormState.guardianTitle}
                  onChange={handleFieldChange}
                  collection={titleOptions}
                  placeholder="Select title"
                  required
                  // error={
                  //   showFieldError("guardianTitle")
                  //     ? getFieldError("guardianTitle")
                  //     : ""
                  // }
                  invalid={showFieldError("guardianTitle")}
                />

                <TextInput
                  label="Full Name"
                  name="guardianFullName"
                  type="text"
                  value={guardianFormState.guardianFullName}
                  onChange={handleFieldChange}
                  placeholder="Enter guardian's full name"
                  required
                  error={
                    showFieldError("guardianFullName")
                      ? getFieldError("guardianFullName")
                      : ""
                  }
                  invalid={showFieldError("guardianFullName")}
                />
              </HStack>

              <HStack
                gap={4}
                w="full"
                flexDirection={["column", "row"]}
                alignItems={["stretch", "start"]}
              >
                <TextInput
                  label="Phone Number"
                  name="guardianPhoneNumber"
                  value={guardianFormState.guardianPhoneNumber}
                  onChange={handleFieldChange}
                  placeholder="Enter phone number"
                  startElement={"+234"}
                  error={
                    getFieldError("guardianPhoneNumber")
                      ? getFieldError("guardianPhoneNumber")
                      : ""
                  }
                  invalid={showFieldError("guardianPhoneNumber")}
                  type="tel"
                  required
                />

                <TextInput
                  label="Email Address"
                  name="guardianEmail"
                  type="email"
                  value={guardianFormState.guardianEmail}
                  onChange={handleFieldChange}
                  placeholder="Enter email address"
                  required
                  error={
                    showFieldError("guardianEmail")
                      ? getFieldError("guardianEmail")
                      : ""
                  }
                  invalid={showFieldError("guardianEmail")}
                />
              </HStack>

              <TextInput
                label="Address"
                name="guardianAddress"
                value={guardianFormState.guardianAddress}
                onChange={handleFieldChange}
                placeholder="Enter guardian's address"
                required
                error={
                  showFieldError("guardianAddress")
                    ? getFieldError("guardianAddress")
                    : ""
                }
                invalid={showFieldError("guardianAddress")}
                type="address"
              />
            </VStack>
          </Box>
        </VStack>
      </CustomModal>
    </>
  );
};

export default AddGuardianModal;
